import { OpensearchBooleanQuery } from "@redotech/opensearch-types/types";
import * as z from "zod/v4";
import { ZodType } from "zod/v4";

// Filter group operator for combining clauses
export enum FilterOperator {
  AND = "and",
  OR = "or",
}

// Generic filter clause - can be a leaf filter or a nested group
export type GenericFilterClause<T> = T | GenericFilterGroup<T>;

// Generic filter group with an operator and nested clauses
export interface GenericFilterGroup<T> {
  operator: FilterOperator;
  clauses: GenericFilterClause<T>[];
}

export enum AdvancedFilterType {
  DATE = "date",
  ARRAY_TO_STRING = "array_to_string",
  NUMBER = "number",
  /**
   * @deprecated Use ARRAY_TO_STRING instead. String filter type is kept only for migration purposes.
   */
  STRING = "string",
  EXISTS = "exists",
  BOOLEAN = "boolean",
}

export const createAdvancedFilterDataSchema = <VALUE, OPERATOR extends string>(
  type: AdvancedFilterType,
  valueSchema: ZodType<VALUE>,
  operatorSchema: ZodType<OPERATOR>,
) =>
  z.object({
    type: z.literal(type),
    name: z.string(),
    value: valueSchema.nullish(),
    operator: operatorSchema,
  });

type CreateAdvancedFilterDataSchema<
  VALUE,
  OPERATOR extends string,
> = ReturnType<typeof createAdvancedFilterDataSchema<VALUE, OPERATOR>>;

export type GenericAdvancedFilterData<VALUE, OPERATOR extends string> = z.infer<
  CreateAdvancedFilterDataSchema<VALUE, OPERATOR>
>;

export type AtlasPathMultiAnalyzer = { value: string; multi: string };

// https://www.mongodb.com/docs/atlas/atlas-search/path-construction/
export type AtlasPath =
  | string
  | string[]
  | AtlasPathMultiAnalyzer
  | (string | AtlasPathMultiAnalyzer)[];

export interface GenericFilterBuilder<
  VALUE,
  OPERATOR extends string,
  DATA extends GenericAdvancedFilterData<VALUE, OPERATOR>,
> {
  type: AdvancedFilterType;
  valueSchema: ZodType<VALUE>;
  operatorSchema: ZodType<OPERATOR>;
  schema: CreateAdvancedFilterDataSchema<VALUE, OPERATOR>;
  addOpensearchFilters: (config: {
    filter: DATA;
    opensearchPath: string;
    keywordPath?: string;
    useObjectId?: boolean;
    timeZoneId?: string | null;
    compound: OpensearchBooleanQuery;
  }) => void;
  readFromString: (
    name: string,
    operatorValue: string,
    defaultFilter: DATA,
  ) => DATA | undefined;
  writeToString: (filter: DATA | undefined) => string;
  isPartial: (filter: DATA) => boolean;
}

// Type guard to check if a FilterClause is a FilterGroup (has operator and clauses)
export function isFilterGroup<T>(
  clause: GenericFilterClause<T>,
): clause is GenericFilterGroup<T> {
  return (
    typeof clause === "object" &&
    clause !== null &&
    "operator" in clause &&
    "clauses" in clause
  );
}

// Type guard to check if a FilterClause is a leaf filter (has type and name)
export function isLeafFilter<T>(
  clause: GenericFilterClause<T>,
): clause is T & { type: string; name: string } {
  return (
    typeof clause === "object" &&
    clause !== null &&
    "type" in clause &&
    "name" in clause
  );
}

// URL encoding constants for filter groups
const GROUP_START = "(";
const GROUP_END = ")";
const OPERATOR_SEPARATOR = ":";
const CLAUSE_SEPARATOR = ";;";

// Encode a filter group to URL string format
// Format: (operator:clause1;;clause2;;...) where clauses can be nested groups
export function encodeFilterGroup<T>(
  group: GenericFilterGroup<T>,
  encodeLeafFilter: (filter: T) => string,
): string {
  const encodedClauses = group.clauses.map((clause) => {
    if (isFilterGroup(clause)) {
      return encodeFilterGroup(clause, encodeLeafFilter);
    }
    return encodeLeafFilter(clause);
  });

  return `${GROUP_START}${group.operator}${OPERATOR_SEPARATOR}${encodedClauses.join(CLAUSE_SEPARATOR)}${GROUP_END}`;
}

// Decode a URL string to filter group
// Returns null if parsing fails
export function decodeFilterGroup<T>(
  encoded: string,
  decodeLeafFilter: (encoded: string) => T | null,
): GenericFilterGroup<T> | null {
  const trimmed = encoded.trim();

  // Check if it's a group (starts with parenthesis)
  if (!trimmed.startsWith(GROUP_START) || !trimmed.endsWith(GROUP_END)) {
    return null;
  }

  // Remove outer parentheses
  const inner = trimmed.slice(1, -1);

  // Find operator separator
  const operatorEndIndex = inner.indexOf(OPERATOR_SEPARATOR);
  if (operatorEndIndex === -1) {
    return null;
  }

  const operatorStr = inner.slice(0, operatorEndIndex);
  const operator =
    operatorStr === FilterOperator.AND
      ? FilterOperator.AND
      : operatorStr === FilterOperator.OR
        ? FilterOperator.OR
        : null;

  if (!operator) {
    return null;
  }

  const clausesStr = inner.slice(operatorEndIndex + 1);
  const clauses: GenericFilterClause<T>[] = [];

  // Parse clauses, handling nested groups
  let currentClause = "";
  let depth = 0;

  for (let i = 0; i < clausesStr.length; i++) {
    const char = clausesStr[i];

    if (char === GROUP_START) {
      depth++;
      currentClause += char;
    } else if (char === GROUP_END) {
      depth--;
      currentClause += char;
    } else if (
      clausesStr.slice(i, i + CLAUSE_SEPARATOR.length) === CLAUSE_SEPARATOR &&
      depth === 0
    ) {
      // Found clause separator at top level
      if (currentClause) {
        const parsed = parseClause(currentClause, decodeLeafFilter);
        if (parsed) {
          clauses.push(parsed);
        }
      }
      currentClause = "";
      i += CLAUSE_SEPARATOR.length - 1; // Skip separator
    } else {
      currentClause += char;
    }
  }

  // Don't forget the last clause
  if (currentClause) {
    const parsed = parseClause(currentClause, decodeLeafFilter);
    if (parsed) {
      clauses.push(parsed);
    }
  }

  return { operator, clauses };
}

function parseClause<T>(
  clauseStr: string,
  decodeLeafFilter: (encoded: string) => T | null,
): GenericFilterClause<T> | null {
  const trimmed = clauseStr.trim();

  // Check if it's a nested group
  if (trimmed.startsWith(GROUP_START)) {
    return decodeFilterGroup(trimmed, decodeLeafFilter);
  }

  // It's a leaf filter
  return decodeLeafFilter(trimmed);
}

// Convert legacy flat filters array to a filter group (AND)
export function filtersToFilterGroup<T>(filters: T[]): GenericFilterGroup<T> {
  return { operator: FilterOperator.AND, clauses: filters };
}
